import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const PeerEndorsements = () => {
  const [selectedEndorsement, setSelectedEndorsement] = useState(null);

  const endorsements = [
    {
      id: 1,
      endorser: "Sarah Chen",
      position: "Senior Frontend Developer",
      company: "TechCorp Solutions",
      relationship: "Former Colleague",
      avatar: "https://randomuser.me/api/portraits/women/32.jpg",
      skills: ["React", "JavaScript", "UI/UX Design"],
      endorsement: `Lahiru is an exceptional developer with a keen eye for detail and user experience. His React skills are top-notch, and he consistently delivers clean, maintainable code. Working with him on the e-commerce platform was a pleasure - he brought innovative solutions to complex problems and mentored junior developers effectively.`,
      rating: 5,
      date: "2024-06-15",
      projectsWorkedTogether: ["E-Commerce Platform", "Admin Dashboard"],
      linkedinUrl: "https://linkedin.com/in/sarahchen"
    },
    {
      id: 2,
      endorser: "Michael Rodriguez",
      position: "DevOps Engineer",
      company: "CloudScale Inc.",
      relationship: "Project Collaborator",
      avatar: "https://randomuser.me/api/portraits/men/45.jpg",
      skills: ["Node.js", "AWS", "Docker"],
      endorsement: `I've worked with Lahiru on multiple cloud deployment projects, and his understanding of both development and DevOps practices is impressive. He writes backend code that's not only functional but also optimized for deployment and scaling. His Docker containerization skills saved us countless hours in production deployments.`,
      rating: 5,
      date: "2024-05-20",
      projectsWorkedTogether: ["Microservices Migration", "CI/CD Pipeline"],
      linkedinUrl: "https://linkedin.com/in/michaelrodriguez"
    },
    {
      id: 3,
      endorser: "Dr. Priya Patel",
      position: "Technical Lead",
      company: "DataViz Analytics",
      relationship: "Client",
      avatar: "https://randomuser.me/api/portraits/women/28.jpg",
      skills: ["Python", "Data Visualization", "API Development"],
      endorsement: `Lahiru delivered an outstanding analytics dashboard for our data science team. His ability to translate complex data requirements into intuitive visualizations is remarkable. The Python backend he built handles millions of data points efficiently, and the React frontend provides an excellent user experience for our analysts.`,
      rating: 5,
      date: "2024-04-10",
      projectsWorkedTogether: ["Analytics Dashboard", "Data Processing Pipeline"],
      linkedinUrl: "https://linkedin.com/in/priyapatel"
    },
    {
      id: 4,
      endorser: "James Wilson",
      position: "Full Stack Developer",
      company: "StartupHub",
      relationship: "Peer Developer",
      avatar: "https://randomuser.me/api/portraits/men/38.jpg",
      skills: ["MongoDB", "Express.js", "System Architecture"],
      endorsement: `Lahiru's database design and backend architecture skills are exceptional. He helped optimize our MongoDB queries, reducing response times by 60%. His systematic approach to problem-solving and code reviews significantly improved our team's overall code quality. A true professional who elevates everyone around him.`,
      rating: 5,
      date: "2024-03-25",
      projectsWorkedTogether: ["Database Optimization", "API Redesign"],
      linkedinUrl: "https://linkedin.com/in/jameswilson"
    },
    {
      id: 5,
      endorser: "Lisa Thompson",
      position: "Product Manager",
      company: "InnovateTech",
      relationship: "Cross-functional Collaborator",
      avatar: "https://randomuser.me/api/portraits/women/42.jpg",
      skills: ["Project Management", "Technical Communication", "Problem Solving"],
      endorsement: `Working with Lahiru from a product perspective has been fantastic. He understands business requirements quickly and translates them into technical solutions effectively. His communication skills bridge the gap between technical and non-technical stakeholders beautifully. He's proactive in suggesting improvements and always delivers on time.`,
      rating: 5,
      date: "2024-02-18",
      projectsWorkedTogether: ["Product Feature Development", "User Experience Optimization"],
      linkedinUrl: "https://linkedin.com/in/lisathompson"
    },
    {
      id: 6,
      endorser: "Alex Kumar",
      position: "Senior Software Engineer",
      company: "TechFlow Systems",
      relationship: "Mentor",
      avatar: "https://randomuser.me/api/portraits/men/35.jpg",
      skills: ["System Design", "Performance Optimization", "Code Review"],
      endorsement: `I had the pleasure of mentoring Lahiru early in his career, and watching his growth has been remarkable. His dedication to learning and applying best practices is exceptional. He's evolved into a skilled full-stack developer who can handle complex system designs and performance optimizations with confidence.`,
      rating: 5,
      date: "2024-01-30",
      projectsWorkedTogether: ["System Architecture Review", "Performance Optimization"],
      linkedinUrl: "https://linkedin.com/in/alexkumar"
    }
  ];

  const skillEndorsementCounts = {
    "React": 15,
    "Node.js": 12,
    "JavaScript": 18,
    "Python": 8,
    "AWS": 10,
    "MongoDB": 9,
    "Docker": 7,
    "TypeScript": 11,
    "Express.js": 8,
    "PostgreSQL": 6,
    "Redis": 5,
    "GraphQL": 4
  };

  const getRelationshipColor = (relationship) => {
    const colorMap = {
      'Former Colleague': 'bg-primary/10 text-primary border-primary/20',
      'Project Collaborator': 'bg-success/10 text-success border-success/20',
      'Client': 'bg-brand-purple/10 text-brand-purple border-brand-purple/20',
      'Peer Developer': 'bg-accent/10 text-accent border-accent/20',
      'Cross-functional Collaborator': 'bg-warning/10 text-warning border-warning/20',
      'Mentor': 'bg-secondary/10 text-secondary border-secondary/20'
    };
    return colorMap[relationship] || 'bg-surface text-text-secondary border-border';
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Icon
        key={index}
        name={index < rating ? 'Star' : 'Star'}
        size={16}
        className={index < rating ? 'text-warning fill-current' : 'text-border'}
      />
    ));
  };

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-text-primary mb-4">Peer Endorsements & Recommendations</h2>
        <p className="text-lg text-text-secondary max-w-3xl mx-auto">
          Professional recommendations from colleagues, clients, and collaborators who have experienced 
          my technical expertise and work ethic firsthand.
        </p>
      </div>

      {/* Skill Endorsement Summary */}
      <div className="bg-card rounded-xl border border-border shadow-premium p-6">
        <h3 className="text-xl font-bold text-text-primary mb-4">Skill Endorsements Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {Object.entries(skillEndorsementCounts)
            .sort(([,a], [,b]) => b - a)
            .map(([skill, count]) => (
              <div key={skill} className="flex items-center justify-between p-3 bg-surface rounded-lg border border-border">
                <span className="font-medium text-text-primary">{skill}</span>
                <div className="flex items-center space-x-2">
                  <Icon name="Users" size={16} className="text-primary" />
                  <span className="text-sm font-bold text-primary">{count}</span>
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* Endorsements Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {endorsements.map((endorsement) => (
          <div
            key={endorsement.id}
            className="bg-card rounded-xl border border-border shadow-premium hover:shadow-lg transition-premium hover-lift cursor-pointer"
            onClick={() => setSelectedEndorsement(endorsement)}
          >
            <div className="p-6">
              <div className="flex items-start space-x-4 mb-4">
                <div className="relative">
                  <Image
                    src={endorsement.avatar}
                    alt={endorsement.endorser}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-success rounded-full border-2 border-white flex items-center justify-center">
                    <Icon name="Check" size={12} className="text-white" />
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="font-bold text-text-primary">{endorsement.endorser}</h4>
                  <p className="text-sm text-text-secondary">{endorsement.position}</p>
                  <p className="text-sm text-text-muted">{endorsement.company}</p>
                  <div className={`inline-flex px-2 py-1 rounded-full text-xs font-medium border mt-2 ${getRelationshipColor(endorsement.relationship)}`}>
                    {endorsement.relationship}
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-1 mb-3">
                {renderStars(endorsement.rating)}
                <span className="text-sm text-text-secondary ml-2">
                  {new Date(endorsement.date).toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'short', 
                    day: 'numeric' 
                  })}
                </span>
              </div>

              <p className="text-text-secondary mb-4 line-clamp-4">
                {endorsement.endorsement}
              </p>

              <div className="space-y-3">
                <div>
                  <h5 className="font-medium text-text-primary mb-2">Endorsed Skills:</h5>
                  <div className="flex flex-wrap gap-1">
                    {endorsement.skills.map((skill, idx) => (
                      <span
                        key={idx}
                        className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full border border-primary/20"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <h5 className="font-medium text-text-primary mb-2">Projects Collaborated:</h5>
                  <div className="flex flex-wrap gap-1">
                    {endorsement.projectsWorkedTogether.map((project, idx) => (
                      <span
                        key={idx}
                        className="text-xs px-2 py-1 bg-success/10 text-success rounded-full border border-success/20"
                      >
                        {project}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <button className="w-full mt-4 px-4 py-2 bg-primary/10 text-primary rounded-lg border border-primary/20 hover:bg-primary/20 transition-premium flex items-center justify-center space-x-2">
                <Icon name="ExternalLink" size={16} />
                <span>Read Full Recommendation</span>
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Endorsement Detail Modal */}
      {selectedEndorsement && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-border p-6 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-text-primary">Professional Recommendation</h2>
              <button
                onClick={() => setSelectedEndorsement(null)}
                className="p-2 hover:bg-surface-hover rounded-lg transition-premium"
              >
                <Icon name="X" size={24} className="text-text-secondary" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="flex items-start space-x-6">
                <div className="relative">
                  <Image
                    src={selectedEndorsement.avatar}
                    alt={selectedEndorsement.endorser}
                    className="w-24 h-24 rounded-full object-cover"
                  />
                  <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-success rounded-full border-3 border-white flex items-center justify-center">
                    <Icon name="Check" size={16} className="text-white" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-text-primary">{selectedEndorsement.endorser}</h3>
                  <p className="text-lg text-text-secondary">{selectedEndorsement.position}</p>
                  <p className="text-text-muted">{selectedEndorsement.company}</p>
                  <div className={`inline-flex px-3 py-1 rounded-full text-sm font-medium border mt-3 ${getRelationshipColor(selectedEndorsement.relationship)}`}>
                    {selectedEndorsement.relationship}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  {renderStars(selectedEndorsement.rating)}
                  <span className="text-lg font-semibold text-text-primary ml-2">
                    {selectedEndorsement.rating}/5
                  </span>
                </div>
                <span className="text-text-secondary">
                  {new Date(selectedEndorsement.date).toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </span>
              </div>

              <div className="bg-surface rounded-lg p-6 border-l-4 border-primary">
                <Icon name="Quote" size={24} className="text-primary mb-4" />
                <p className="text-text-primary text-lg leading-relaxed italic">
                  "{selectedEndorsement.endorsement}"
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-text-primary mb-3">Skills Endorsed</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedEndorsement.skills.map((skill, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 bg-primary/10 text-primary rounded-full border border-primary/20"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-text-primary mb-3">Collaborative Projects</h4>
                  <div className="space-y-2">
                    {selectedEndorsement.projectsWorkedTogether.map((project, idx) => (
                      <div key={idx} className="flex items-center space-x-2">
                        <Icon name="Briefcase" size={16} className="text-success" />
                        <span className="text-text-secondary">{project}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex space-x-4">
                <button className="flex-1 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-premium flex items-center justify-center space-x-2">
                  <Icon name="ExternalLink" size={20} />
                  <span>View LinkedIn Profile</span>
                </button>
                <button className="px-6 py-3 bg-surface text-text-primary rounded-lg border border-border hover:bg-surface-hover transition-premium flex items-center justify-center space-x-2">
                  <Icon name="MessageCircle" size={20} />
                  <span>Contact</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-card rounded-xl border border-border p-6 text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-success to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Users" size={32} className="text-white" />
          </div>
          <h3 className="text-2xl font-bold text-text-primary mb-2">{endorsements.length}</h3>
          <p className="text-text-secondary">Professional Recommendations</p>
        </div>

        <div className="bg-card rounded-xl border border-border p-6 text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-primary to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Star" size={32} className="text-white" />
          </div>
          <h3 className="text-2xl font-bold text-text-primary mb-2">5.0</h3>
          <p className="text-text-secondary">Average Rating</p>
        </div>

        <div className="bg-card rounded-xl border border-border p-6 text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-brand-purple to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Award" size={32} className="text-white" />
          </div>
          <h3 className="text-2xl font-bold text-text-primary mb-2">{Object.keys(skillEndorsementCounts).length}</h3>
          <p className="text-text-secondary">Skills Endorsed</p>
        </div>

        <div className="bg-card rounded-xl border border-border p-6 text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-accent to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Briefcase" size={32} className="text-white" />
          </div>
          <h3 className="text-2xl font-bold text-text-primary mb-2">12</h3>
          <p className="text-text-secondary">Collaborative Projects</p>
        </div>
      </div>
    </div>
  );
};

export default PeerEndorsements;